/**
 * A seasonal flourish played when a workflow launches from the home page: a few
 * bats take off from wherever the launch was triggered and curve up and away.
 * It is mounted straight on `document.body` so it keeps playing across the
 * navigation to the new workspace, never takes pointer events, and removes
 * itself when done.
 */

// Drawn on a 100x50 box facing the viewer. Only the right wing is drawn; the
// left is its mirror, so one flap animation drives both.
const BAT_BODY =
  '<ellipse cx="50" cy="27" rx="4.5" ry="9"/>' +
  '<circle cx="50" cy="17" r="5"/>' +
  '<path d="M45.6 15 L46.4 8 L49 13.5 Z M54.4 15 L53.6 8 L51 13.5 Z"/>';
const BAT_WING =
  'M53 21 C62 12 79 7 97 13 C91 17 89 22 90 28 C85 24 80 25 77 31 C73 26 67 26 64 32 C61 27 57 27 53 31 Z';
const SHOULDER = '53px 24px';

// One entry per bat: its heading in degrees off vertical (negative is left),
// width in px, and how long after the first it takes off.
const BATS = [
  { heading: -32, size: 40, delay: 0 },
  { heading: 6, size: 34, delay: 110 },
  { heading: 38, size: 28, delay: 210 }
];

const FLIGHT_MS = 1900;
const SAMPLES = 32;
const MAX_TILT = 28;
// How far below the top edge a bat starts to fade, so it is gone by the edge.
const FADE_BAND_PX = 140;
// A launch waits on the createCaraWorkspace round trip, uploads included, so a
// click this old still counts as the one that started it.
const ORIGIN_TTL_MS = 60_000;

type Point = { x: number; y: number };

let playing = false;
let lastOrigin: (Point & { at: number }) | undefined;

// Remember where the user last acted so the bats can take off from there: the
// pointer for a click, the focused control for an Enter in the compose box.
if (typeof document !== 'undefined') {
  document.addEventListener(
    'pointerdown',
    (e) => {
      lastOrigin = { x: e.clientX, y: e.clientY, at: Date.now() };
    },
    { capture: true, passive: true }
  );
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key !== 'Enter' || !(document.activeElement instanceof HTMLElement)) {
        return;
      }
      const rect = document.activeElement.getBoundingClientRect();
      lastOrigin = { x: rect.right - 24, y: rect.top + rect.height / 2, at: Date.now() };
    },
    { capture: true, passive: true }
  );
}

const launchOrigin = (): Point => {
  if (lastOrigin && Date.now() - lastOrigin.at < ORIGIN_TTL_MS) {
    return lastOrigin;
  }
  return { x: window.innerWidth / 2, y: window.innerHeight * 0.6 };
};

const jitter = (amount: number) => (Math.random() * 2 - 1) * amount;

const bezier = (p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point => {
  const u = 1 - t;
  const a = u * u * u;
  const b = 3 * u * u * t;
  const c = 3 * u * t * t;
  const d = t * t * t;
  return {
    x: a * p0.x + b * p1.x + c * p2.x + d * p3.x,
    y: a * p0.y + b * p1.y + c * p2.y + d * p3.y
  };
};

/**
 * A gentle arc from the origin up to the top edge along `heading`, sampled
 * densely so the flight is smooth, with each sample banking toward where the
 * bat is going next. Bats fade out by height, gone just as they reach the top.
 */
const flightKeyframes = (origin: Point, heading: number): Keyframe[] => {
  const rise = Math.max(origin.y, 120);
  const fadeBand = Math.min(FADE_BAND_PX, rise * 0.4);
  const lean = Math.tan((heading * Math.PI) / 180);
  const end = { x: origin.x + rise * lean, y: origin.y - rise };
  // Lift off roughly straight up, then bend out toward the heading, so the
  // bats fan apart from the origin rather than crossing each other's paths.
  const c1 = { x: origin.x + jitter(20), y: origin.y - rise * 0.35 };
  const c2 = { x: origin.x + (end.x - origin.x) * 0.55, y: origin.y - rise * 0.75 };

  const points = Array.from({ length: SAMPLES + 1 }, (_, i) =>
    bezier(origin, c1, c2, end, i / SAMPLES)
  );
  return points.map((p, i) => {
    const t = i / SAMPLES;
    const next = points[Math.min(i + 1, SAMPLES)];
    const prev = points[Math.max(i - 1, 0)];
    const tilt = Math.max(
      -MAX_TILT,
      Math.min(MAX_TILT, (Math.atan2(next.x - prev.x, prev.y - next.y) * 180) / Math.PI)
    );
    // Grows in as it takes off, then shrinks with distance.
    const scale = t < 0.15 ? 0.55 + (t / 0.15) * 0.45 : 1 - (t - 0.15) * 0.35;
    const fadeIn = Math.min(1, t / 0.15);
    const fadeOut = Math.max(0, Math.min(1, (p.y - (origin.y - rise)) / fadeBand));
    const opacity = Math.min(fadeIn, fadeOut);
    return {
      offset: t,
      transform: `translate(${p.x}px, ${p.y}px) rotate(${tilt}deg) scale(${scale})`,
      opacity: opacity * 0.85
    };
  });
};

/**
 * Late October through the end of November 2nd, in the viewer's local time, so
 * it runs through the Monday after Halloween this year.
 */
export const isSpookySeason = (now: Date = new Date()) =>
  (now.getMonth() === 9 && now.getDate() >= 24) || (now.getMonth() === 10 && now.getDate() <= 2);

export const playSpookyLaunch = (now: Date = new Date()) => {
  if (playing || typeof document === 'undefined' || !isSpookySeason(now)) {
    return;
  }
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) {
    return;
  }
  // jsdom and very old browsers have no Web Animations API.
  if (typeof document.body.animate !== 'function') {
    return;
  }

  const dark = document.documentElement.classList.contains('dark');
  const batColor = dark ? 'rgb(196 181 253)' : 'rgb(38 24 56)';
  const origin = launchOrigin();

  const root = document.createElement('div');
  root.setAttribute('aria-hidden', 'true');
  Object.assign(root.style, {
    position: 'fixed',
    inset: '0',
    pointerEvents: 'none',
    zIndex: '9999',
    overflow: 'hidden'
  });

  const flights: Animation[] = [];

  for (const spec of BATS) {
    const size = spec.size * (1 + jitter(0.1));
    const bat = document.createElement('div');
    // Centered on its translate point so the path and the bank pivot line up.
    Object.assign(bat.style, {
      position: 'absolute',
      left: `${-size / 2}px`,
      top: `${-size / 4}px`,
      width: `${size}px`,
      height: `${size / 2}px`,
      opacity: '0'
    });
    const wing = `<g class="wing" style="transform-box: view-box; transform-origin: ${SHOULDER}"><path d="${BAT_WING}"/></g>`;
    bat.innerHTML =
      `<svg viewBox="0 0 100 50" width="100%" height="100%" fill="${batColor}" overflow="visible">` +
      `${BAT_BODY}${wing}<g transform="matrix(-1 0 0 1 100 0)">${wing}</g></svg>`;
    root.appendChild(bat);

    flights.push(
      bat.animate(flightKeyframes(origin, spec.heading + jitter(8)), {
        duration: FLIGHT_MS * (1 + jitter(0.1)),
        delay: spec.delay + jitter(30),
        easing: 'cubic-bezier(0.35, 0, 0.65, 1)',
        fill: 'backwards'
      })
    );

    // A quick downstroke and a slower recovery, at a rate and phase of each
    // bat's own so the three never flap in lockstep.
    const flapMs = 230 + jitter(35);
    const phase = Math.random();
    bat
      .querySelectorAll('.wing')
      .forEach((el) =>
        el.animate(
          [
            { transform: 'rotate(-30deg)', easing: 'ease-in' },
            { transform: 'rotate(20deg)', offset: 0.35, easing: 'ease-out' },
            { transform: 'rotate(-30deg)' }
          ],
          { duration: flapMs, iterations: Infinity, iterationStart: phase }
        )
      );
  }

  playing = true;
  document.body.appendChild(root);

  const cleanup = () => {
    playing = false;
    root.remove();
  };
  Promise.all(flights.map((a) => a.finished)).then(cleanup, cleanup);
};
